import {useMemo} from 'react';
import AppTable, {ColumnDef} from '../../../../../components/common/AppTable';
import AppTextInput from '../../../../../components/common/AppTextInput';
import {SVG} from '../../../../../constants/assetLists';
import {UserItem, UsersListResponse} from '../../UserScreens/users';
import {ICONS} from '../../../data';
import {getRoleTypeName} from '../../../../../utils';

function UsersList({
  allUsers,
  searchText,
  setSearchText,
  debouncedSearch,
  users,
  onUserSelection,
  error,
  marginStart = true,
  maxHeight = '300px',
}: {
  allUsers: UsersListResponse | undefined;
  searchText: string;
  setSearchText: (text: string) => void;
  debouncedSearch: string;
  users: string[];
  onUserSelection: (userIds: Array<any>[]) => void;
  error?: string;
  marginStart?: boolean;
  maxHeight?: string;
}) {
  const columns = useMemo<ColumnDef<UserItem>[]>(
    () => [
      {
        title: 'שם',
        field: 'username',
        accessor: r => r?.firstName + ' ' + r?.lastName,
        sortable: false,
      },
      {
        title: 'תפקיד',
        field: 'role',
        accessor: r => r?.roles[0],
        sortable: false,
        render: r => {
          const icon =
            ICONS?.find(icon => icon.id === r?.roles[0]?.icon)?.icon || '';
          return (
            <div className="flex items-center gap-2">
              {icon && (
                <img
                  src={icon}
                  alt="icon"
                  className="w-[20px] h-[20px] object-contain"
                />
              )}
              <span>{getRoleTypeName(r?.roles[0]?.roleType) || '-'}</span>
            </div>
          );
        },
      },
      {
        title: 'מיקום',
        field: 'clientId',
        accessor: r => r?.clientId?.map(c => c.name).join(', ') || '-',
        sortable: false,
      },
    ],
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <div className={`${marginStart ? 'ms-10' : ''} flex flex-col gap-3`}>
      <p className="m-0">משתמשים משויכים לקבוצה ({users?.length || 0})</p>

      {error && <span className="text-raspberryRed text-sm">{error}</span>}

      <AppTextInput
        placeholder="חיפוש משתמש"
        value={searchText}
        onChange={handleSearch}
        className="bg-transparent !rounded-[28px] pl-16"
        endIcon={
          <div className="flex items-center gap-2">
            {searchText && (
              <div
                onClick={() => setSearchText('')}
                className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer">
                <img
                  src={SVG.closeDarkIcon}
                  alt="Clear"
                  className="w-[12px] h-[12px] object-contain"
                />
              </div>
            )}
            <div
              className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                searchText && 'bg-aqua'
              }`}>
              <img
                src={SVG.searchIcon}
                alt="Search"
                className="w-[18px] h-[18px] object-contain"
              />
            </div>
          </div>
        }
      />

      {!allUsers?.data?.length && (
        <div className="bg-pastelYellow rounded-lg p-1">
          <p className="text-lg font-semibold text-black m-0 text-center">
            לא נמצאו תוצאות לתצוגה
          </p>
        </div>
      )}

      <div className={`flex`} style={{maxHeight: maxHeight}}>
        <AppTable<UserItem>
          data={allUsers?.data || []}
          columns={columns}
          rowKey={r => r?._id}
          selectable
          selectedKeys={users || []}
          onSelectionChange={ids => onUserSelection(ids)}
          searchText={debouncedSearch}
        />
      </div>
    </div>
  );
}

export default UsersList;
